import Phaser from 'phaser';
import { findPath, adjacentWalkable } from '../world/pathfinding';
import { faceMove, faceIdle, type Facing } from '../world/playerFacing';
import { trailSign } from '../world/trailSign';
import { TX, AUDIO } from '../registry/keys';
import { CONFIG } from '../data/config';
import { playSfx } from '../audio/sfx';
import { store } from '../state/store';
import { tileKey, type TileXY } from '../types';
import { getHat, hatTexture } from '../data/hats';
import type { UIScene } from './UIScene';

const TS = CONFIG.tileSize;
const TW = 16;
const TH = 11;
const EXIT: TileXY = { x: 7, y: 10 }; // trail back to the forest (bottom gap)
const ENTRY: TileXY = { x: 7, y: 9 };
const WELL: TileXY = { x: 8, y: 5 };
const BOOTH: TileXY = { x: 3, y: 5 };
const VENDOR: TileXY = { x: 5, y: 8 }; // a traveling vendor visits on market days
const NOTICE: TileXY = { x: 11, y: 8 }; // the town notice board (daily request)
const ORIGIN_Y = 0.7;

interface Building {
  x: number;
  y: number;
  w: number;
  h: number;
  tex: string;
}
const BUILDINGS: Building[] = [
  { x: 2, y: 1, w: 3, h: 3, tex: TX.shop },
  { x: 7, y: 1, w: 3, h: 3, tex: TX.house },
  { x: 12, y: 1, w: 3, h: 3, tex: TX.house2 },
];

interface Npc {
  x: number;
  y: number;
  tex: string;
  name: string;
  lines: string[];
}
const NPCS: Npc[] = [
  { x: 4, y: 6, tex: TX.npcA, name: 'Rosie', lines: ['Fresh bread today! 🍞', "Lovely weather, isn't it?", 'Your farm is the talk of the town!'] },
  { x: 10, y: 7, tex: TX.npcB, name: 'Sam', lines: ['Caught a big one yesterday! 🎣', "The river's been good to me.", 'Come fishing sometime!'] },
  { x: 12, y: 6, tex: TX.npcC, name: 'Old Joe', lines: ['Back in my day... 👴', 'Take care of that land, kiddo.', 'A good harvest is its own reward.'] },
];

/** The town: a little village green with shops, a well, and folk to greet. Trail back to the forest. */
export class TownScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private blocked = new Set<string>();
  private path: TileXY[] = [];
  private pendingArrival: (() => void) | null = null;
  private playerTile: TileXY = { ...ENTRY };
  private leaving = false;
  private hat?: Phaser.GameObjects.Image;
  private lastFacing: Facing = 'down';
  private npcSprites: Phaser.GameObjects.Image[] = [];
  private vendorHere = false;

  constructor() {
    super('Town');
  }

  private get ui(): UIScene {
    return this.scene.get('UI') as UIScene;
  }

  create(): void {
    this.leaving = false;
    this.blocked.clear();
    this.path = [];
    this.npcSprites = [];
    this.cameras.main.setBackgroundColor('#a8d0e8');
    const short = Math.min(this.scale.width, this.scale.height);
    this.cameras.main.setZoom(Phaser.Math.Clamp(Math.round(short / (TW * TS)), CONFIG.minZoom, CONFIG.maxZoom));
    this.cameras.main.centerOn((TW * TS) / 2, (TH * TS) / 2);

    this.buildTownMap();

    this.player = this.add.sprite(ENTRY.x * TS + TS / 2, ENTRY.y * TS + TS, TX.player, 0).setOrigin(0.5, ORIGIN_Y);
    this.player.setDepth(ENTRY.y * TS + TS);
    this.playerTile = { ...ENTRY };
    this.applyHat();

    this.add
      .text(this.scale.width / 2, 8, 'Town — say hello, then tap the trail back', { fontFamily: 'monospace', fontSize: '11px', color: '#2c5a6a' })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setResolution(2)
      .setDepth(1000);

    this.input.on('pointerdown', this.onTap, this);
    this.time.addEvent({ delay: 2400, loop: true, callback: () => this.wanderNpcs() });

    if (!store.get().flags['visited_town']) {
      store.update((s) => (s.flags['visited_town'] = true));
      store.logJournal('Visited the town for the first time 🏘️');
      this.time.delayedCall(600, () => this.ui.showToast('Welcome to town! Say hi to the neighbors 🏘️', 'good'));
    }
  }

  private buildTownMap(): void {
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const onPath = x === 7 || x === 8 || y === 7;
        this.add.image(x * TS, y * TS, onPath ? TX.townPath : TX.townGround).setOrigin(0, 0).setDepth(0);
        const border = x === 0 || x === TW - 1 || y === 0 || y === TH - 1;
        if (border && !(x === EXIT.x && y === EXIT.y)) this.blocked.add(tileKey(x, y));
      }
    }
    this.add.image(EXIT.x * TS, EXIT.y * TS, TX.trailArch).setOrigin(0, 0).setDepth(1);
    trailSign(this, EXIT.x, EXIT.y - 1, '🌲 Forest');

    for (const b of BUILDINGS) {
      this.add.image(b.x * TS, b.y * TS, b.tex).setOrigin(0, 0).setDepth((b.y + b.h) * TS);
      for (let dx = 0; dx < b.w; dx++) for (let dy = 0; dy < b.h; dy++) this.blocked.add(tileKey(b.x + dx, b.y + dy));
    }

    this.add.image(WELL.x * TS, WELL.y * TS, TX.well).setOrigin(0, 0).setDepth(WELL.y * TS + TS).setScale(1.2);
    this.blocked.add(tileKey(WELL.x, WELL.y));

    this.add.image(BOOTH.x * TS, BOOTH.y * TS, TX.booth).setOrigin(0, 0).setDepth(BOOTH.y * TS + TS);
    this.blocked.add(tileKey(BOOTH.x, BOOTH.y));
    this.add
      .text(BOOTH.x * TS + TS / 2, BOOTH.y * TS - 3, '🎯 Game', { fontFamily: 'monospace', fontSize: '8px', color: '#ffffff', backgroundColor: '#000000aa', padding: { x: 2, y: 1 } })
      .setOrigin(0.5, 1)
      .setResolution(2)
      .setDepth(90);

    if (store.get().clock.day % 4 === 0) {
      this.vendorHere = true;
      this.add.image(VENDOR.x * TS - 4, VENDOR.y * TS, TX.vendorCart).setOrigin(0, 0).setDepth(VENDOR.y * TS + TS);
      this.blocked.add(tileKey(VENDOR.x, VENDOR.y));
      this.add
        .text(VENDOR.x * TS + TS / 2, VENDOR.y * TS - 3, '🛒 Vendor', { fontFamily: 'monospace', fontSize: '8px', color: '#ffe9a8', backgroundColor: '#000000aa', padding: { x: 2, y: 1 } })
        .setOrigin(0.5, 1)
        .setResolution(2)
        .setDepth(90);
    }

    NPCS.forEach((n, i) => {
      this.npcSprites[i] = this.add.image(n.x * TS + TS / 2, n.y * TS + TS, n.tex).setOrigin(0.5, 1).setDepth(n.y * TS + TS).setScale(1.1);
    });

    this.add.image(NOTICE.x * TS, NOTICE.y * TS, TX.blueprint).setOrigin(0, 0).setDepth(NOTICE.y * TS + TS).setScale(1.1);
    this.blocked.add(tileKey(NOTICE.x, NOTICE.y));
    this.add
      .text(NOTICE.x * TS + TS / 2, NOTICE.y * TS - 3, '📋 Board', { fontFamily: 'monospace', fontSize: '8px', color: '#ffffff', backgroundColor: '#000000aa', padding: { x: 2, y: 1 } })
      .setOrigin(0.5, 1)
      .setResolution(2)
      .setDepth(90);
  }

  private isBlocked = (x: number, y: number): boolean =>
    x < 0 || y < 0 || x >= TW || y >= TH || this.blocked.has(tileKey(x, y));

  private npcTileAt(tile: TileXY): number {
    return NPCS.findIndex((_n, i) => {
      const sp = this.npcSprites[i];
      return !!sp && Math.floor(sp.x / TS) === tile.x && Math.floor((sp.y - 1) / TS) === tile.y;
    });
  }

  private onTap(pointer: Phaser.Input.Pointer): void {
    if (this.leaving || this.ui.isModalOpen() || this.ui.consumesPointer(pointer.x, pointer.y)) return;
    const tile = { x: Math.floor(pointer.worldX / TS), y: Math.floor(pointer.worldY / TS) };
    if (tile.x < 0 || tile.y < 0 || tile.x >= TW || tile.y >= TH) return;

    const ni = this.npcTileAt(tile);
    if (ni >= 0) return this.routeToInteractable(tile, () => this.talkToNpc(ni));
    const store0 = BUILDINGS[0]!; // the General Store — tap to shop
    if (tile.x >= store0.x && tile.x < store0.x + store0.w && tile.y >= store0.y && tile.y < store0.y + store0.h) {
      return this.routeToInteractable({ x: store0.x + 1, y: store0.y + store0.h - 1 }, () => this.ui.openStall());
    }
    if (tile.x === WELL.x && tile.y === WELL.y) return this.routeToInteractable(WELL, () => this.makeWish());
    if (tile.x === BOOTH.x && tile.y === BOOTH.y) return this.routeToInteractable(BOOTH, () => this.ui.openMoleGame());
    if (this.vendorHere && tile.x === VENDOR.x && tile.y === VENDOR.y) return this.routeToInteractable(VENDOR, () => this.ui.openVendor());
    if (tile.x === NOTICE.x && tile.y === NOTICE.y) return this.routeToInteractable(NOTICE, () => this.ui.openQuestBoard());
    if (tile.x === EXIT.x && tile.y === EXIT.y) return this.routeTo(tile, () => this.leave());
    if (!this.isBlocked(tile.x, tile.y)) this.routeTo(tile, null);
  }

  private talkToNpc(i: number): void {
    const n = NPCS[i]!;
    const sp = this.npcSprites[i]!;
    const line = n.lines[Math.floor(Math.random() * n.lines.length)]!;
    const t = this.add
      .text(sp.x, sp.y - 18, `${n.name}: ${line}`, {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#2a2a2a',
        backgroundColor: '#fffef0',
        padding: { x: 4, y: 3 },
        align: 'center',
        wordWrap: { width: 130 },
      })
      .setOrigin(0.5, 1)
      .setDepth(9000)
      .setResolution(2);
    this.time.delayedCall(2800, () => t.destroy());
    playSfx(this, AUDIO.pop);
  }

  private makeWish(): void {
    if (store.get().money < 10) {
      this.ui.showToast('You need a coin (10g) to make a wish', 'info');
      return;
    }
    store.update((s) => (s.money -= 10));
    playSfx(this, AUDIO.coin);
    const w = this.add.image(WELL.x * TS + TS / 2, WELL.y * TS + 4, TX.sparkle).setDepth(9000).setScale(1.2);
    this.tweens.add({ targets: w, y: w.y - 10, alpha: 0, scale: 1.8, duration: 900, onComplete: () => w.destroy() });
    if (Math.random() < 0.3) {
      const reward = Phaser.Math.Between(25, 70);
      store.update((s) => (s.money += reward));
      this.ui.showToast(`✨ Your wish came true! −10g, +${reward}g 💛`, 'good');
    } else {
      this.ui.showToast('You toss a coin in (−10g) and make a wish ✨', 'info');
    }
  }

  private wanderNpcs(): void {
    NPCS.forEach((_n, i) => {
      const sp = this.npcSprites[i];
      if (!sp || Math.random() < 0.45) return;
      const bx = Math.floor(sp.x / TS);
      const by = Math.floor((sp.y - 1) / TS);
      for (let t = 0; t < 5; t++) {
        const nx = Phaser.Math.Clamp(bx + Phaser.Math.Between(-1, 1), 1, TW - 2);
        const ny = Phaser.Math.Clamp(by + Phaser.Math.Between(-1, 1), 1, TH - 2);
        if (!this.isBlocked(nx, ny)) {
          sp.setFlipX(nx * TS + TS / 2 < sp.x);
          this.tweens.add({
            targets: sp,
            x: nx * TS + TS / 2,
            y: ny * TS + TS,
            duration: 800,
            ease: 'Sine.easeInOut',
            onUpdate: () => sp.setDepth(sp.y),
          });
          break;
        }
      }
    });
  }

  private routeTo(tile: TileXY, action: (() => void) | null): void {
    if (this.playerTile.x === tile.x && this.playerTile.y === tile.y) {
      action?.();
      return;
    }
    const path = findPath(this.playerTile, tile, TW, TH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private routeToInteractable(target: TileXY, action: () => void): void {
    if (Math.abs(this.playerTile.x - target.x) + Math.abs(this.playerTile.y - target.y) <= 1) {
      action();
      return;
    }
    const adj = adjacentWalkable(target, this.playerTile, TW, TH, this.isBlocked);
    if (!adj) return;
    const path = findPath(this.playerTile, adj, TW, TH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private leave(): void {
    if (this.leaving) return;
    this.leaving = true;
    playSfx(this, AUDIO.pop);
    this.scene.stop();
    this.scene.resume('Forest');
  }

  applyHat(): void {
    if (this.hat) {
      this.hat.destroy();
      this.hat = undefined;
    }
    const tex = hatTexture(getHat());
    if (tex) {
      this.hat = this.add.image(this.player.x, this.player.y - 16, tex).setOrigin(0.5, 0.5).setDepth(this.player.depth + 1);
    }
  }

  update(_time: number, delta: number): void {
    if (this.hat) {
      this.hat.setPosition(this.player.x, this.player.y - 16).setFlipX(this.player.flipX).setDepth(this.player.depth + 1);
    }
    if (this.path.length === 0) return;
    const target = this.path[0]!;
    const fx = target.x * TS + TS / 2;
    const fy = target.y * TS + TS;
    const dx = fx - this.player.x;
    const dy = fy - this.player.y;
    const dist = Math.hypot(dx, dy);
    this.lastFacing = faceMove(this, this.player, dx, dy);
    const step = (CONFIG.walkTilesPerSecond * TS * delta) / 1000;
    if (dist <= step || dist === 0) {
      this.player.setPosition(fx, fy);
      this.player.setDepth(fy);
      this.playerTile = { x: target.x, y: target.y };
      this.path.shift();
      if (this.path.length === 0) {
        faceIdle(this, this.player, this.lastFacing);
        const a = this.pendingArrival;
        this.pendingArrival = null;
        a?.();
      }
    } else {
      this.player.x += (dx / dist) * step;
      this.player.y += (dy / dist) * step;
      this.player.setDepth(this.player.y);
    }
  }
}
